import { Link } from "react-router-dom";
import { ArrowUpRight, Factory, HeartPulse, ShoppingCart, Landmark, Zap, GraduationCap } from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { INDUSTRIES } from "../data/services";

const ICONS = { Factory, HeartPulse, ShoppingCart, Landmark, Zap, GraduationCap };

export default function Industries() {
    return (
        <>
            <Seo
                title="Industries"
                description="Innovated Global IT Solutions supports organizations with different business needs — systems, software, digital marketing and AI that adapt to your sector."
            />

            <section className="bg-grid relative overflow-hidden py-20 lg:py-24" data-testid="industries-hero">
                <div
                    className="pointer-events-none absolute inset-0"
                    style={{ background: "radial-gradient(900px 480px at 20% 0%, rgba(0,240,255,0.10), transparent 60%), radial-gradient(700px 420px at 90% 90%, rgba(112,0,255,0.10), transparent 60%)" }}
                />
                <div className="container-x relative max-w-3xl">
                    <Reveal>
                        <p className="eyebrow text-cyan-300">Industries</p>
                        <h1 className="mt-4 text-4xl font-bold tracking-tight sm:text-5xl">
                            Different industries. Different needs. One adaptable team.
                        </h1>
                        <p className="mt-6 text-base leading-relaxed text-slate-400 sm:text-lg">
                            Every organization runs on its own mix of systems, customers and constraints. Rather
                            than claiming one-size-fits-all experience, we bring four adaptable service pillars —
                            and shape them around the way your organization actually works.
                        </p>
                    </Reveal>
                </div>
            </section>

            <section className="py-16 lg:py-24" data-testid="industries-grid">
                <div className="container-x">
                    <Reveal>
                        <h2 className="max-w-2xl text-2xl font-semibold tracking-tight sm:text-3xl">
                            The kinds of organizations our services are built to support.
                        </h2>
                    </Reveal>
                    <div className="focus-grid mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                        {INDUSTRIES.map((ind, i) => {
                            const Icon = ICONS[ind.icon];
                            const accent = ["#0066FF", "#E6007E", "#7000FF", "#00F0FF"][i % 4];
                            return (
                                <Reveal key={ind.name} delay={i * 0.05}>
                                    <div
                                        className="focus-card glass group h-full rounded-3xl p-7"
                                        style={{ "--accent": accent, "--accent-soft": `${accent}24` }}
                                        data-testid={`industries-card-${i}`}
                                    >
                                        <span
                                            className="inline-flex h-12 w-12 items-center justify-center rounded-2xl transition-transform duration-300 group-hover:scale-110"
                                            style={{ background: `${accent}22`, color: accent }}
                                        >
                                            <Icon size={22} aria-hidden="true" />
                                        </span>
                                        <h3 className="mt-5 text-lg font-semibold">{ind.name}</h3>
                                        <p className="mt-2 text-sm leading-relaxed text-slate-400">{ind.desc}</p>
                                    </div>
                                </Reveal>
                            );
                        })}
                    </div>

                    <Reveal className="mt-14">
                        <div className="glass flex flex-col items-start justify-between gap-6 rounded-3xl p-8 sm:p-10 lg:flex-row lg:items-center">
                            <div>
                                <h2 className="text-xl font-semibold sm:text-2xl">Don't see your industry listed?</h2>
                                <p className="mt-2 max-w-lg text-sm leading-relaxed text-slate-400">
                                    Our work starts with understanding your business need — not fitting you into a
                                    template. Tell us about your situation.
                                </p>
                            </div>
                            <Link to="/contact" className="btn-primary shrink-0" data-testid="industries-cta-button">
                                Let's Talk
                                <ArrowUpRight size={16} aria-hidden="true" />
                            </Link>
                        </div>
                    </Reveal>
                </div>
            </section>
        </>
    );
}
